import { Component, OnInit, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { UserContextService } from '../../services/user-context.service';
import { SidebarVisibilityService } from '../../services/sidebar-visibility.service';
import { OfacRecordsService } from '../../services/ofac-records.service';
import { FlaggedContactHeaderModel } from '../../models/flagged-contact-header.model';
import { FlaggedContactSubContactModel } from '../../models/flagged-contact-sub-contact.model';

@Component({
  selector: 'app-flagged-contacts',
  templateUrl: './flagged-contacts.component.html',
  styleUrls: ['./flagged-contacts.component.css'],
  standalone: false
})
export class FlaggedContactsComponent implements OnInit {
  contextMenuRecord: FlaggedContactHeaderModel | null = null;
  contextMenuVisible = false;
  contextMenuX = 0;
  contextMenuY = 0;
  sourceId: string | null = null;
  headerRecords: FlaggedContactHeaderModel[] | null = null;
  subRecords: FlaggedContactSubContactModel[] | null = null;
  selectedHeaderRecord: FlaggedContactHeaderModel | null = null;
  selectedSubRecord: FlaggedContactSubContactModel | null = null;
  isLoading = false;
  isExcluding = false;
  isSyncingStatus = false;
  showFlyout = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  
  // Sorting state for header grid (default to lastName to match service sorting)
  headerSortColumn: string | null = 'lastName';
  headerSortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private ofacRecordsService: OfacRecordsService,
    public userContext: UserContextService,
    private sidebarVisibilityService: SidebarVisibilityService,
    private router: Router
  ) {}

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeContextMenu();
  }

  async ngOnInit(): Promise<void> {
    // Ensure sidebar is visible on this page
    if (this.sidebarVisibilityService) {
      this.sidebarVisibilityService.showSidebar = true;
    }

    try {
      // Ensure we have a sourceId, redirect/re-authenticate if needed
      this.sourceId = await this.userContext.ensureSourceIdAsync();

      if (!this.sourceId) {
        // User was redirected or re-authentication failed, don't load data
        return;
      }

      // Load data for the flagged contacts
      await this.loadData();
    } catch (ex) {
      this.errorMessage = `Error initializing page: ${ex}`;
      // Try to ensure sourceId one more time
      this.sourceId = await this.userContext.ensureSourceIdAsync();
      if (this.sourceId) {
        await this.loadData();
      }
    }
  }

  private async loadData(): Promise<void> {
    if (!this.sourceId) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.headerRecords = null;
    this.subRecords = null;
    this.selectedHeaderRecord = null;
    this.selectedSubRecord = null;
    this.showFlyout = false;
    this.closeContextMenu();

    try {
      this.ofacRecordsService.getFlaggedContactHeaderRecords(this.sourceId).subscribe({
        next: (records) => {
          this.headerRecords = records;
          this.applyHeaderSorting();

          // Auto-select first header record to show sub records and details
          if (this.headerRecords && this.headerRecords.length > 0) {
            this.onHeaderRowSelected(this.headerRecords[0]);
          }
          this.isLoading = false;
        },
        error: (error) => {
          const errorDetails = error?.message || error?.toString() || 'Unknown error';
          const errorStatus = error?.status ? ` (Status: ${error.status})` : '';
          const errorUrl = error?.url ? ` URL: ${error.url}` : '';
          this.errorMessage = `Error loading data: ${errorDetails}${errorStatus}${errorUrl}`;
          this.isLoading = false;
        }
      });
    } catch (ex) {
      this.errorMessage = `Error loading data: ${ex}`;
      this.isLoading = false;
    }
  }

  closeContextMenu(): void {
    this.contextMenuVisible = false;
    this.contextMenuRecord = null;
  }

  onHeaderRowContextMenu(event: MouseEvent, record: FlaggedContactHeaderModel): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.userContext.hasDataWriteRole()) {
      return;
    }

    this.onHeaderRowSelected(record);
    this.contextMenuRecord = record;
    this.contextMenuX = event.clientX;
    this.contextMenuY = event.clientY;
    this.contextMenuVisible = true;
  }

  onHeaderRowSelected(record: FlaggedContactHeaderModel): void {
    // Only reload if selecting a different record
    if (this.selectedHeaderRecord?.contactId === record.contactId) {
      return;
    }

    this.selectedHeaderRecord = record;
    this.showFlyout = false;
    this.selectedSubRecord = null;
    this.subRecords = null; // Clear previous sub-records immediately

    if (!this.sourceId) {
      return;
    }

    // Load sub-records for the selected header record
    this.ofacRecordsService.getFlaggedContactSubRecords(this.sourceId, record.contactId).subscribe({
      next: (records) => {
        this.subRecords = records;

        // Auto-select first sub-record to show details
        if (this.subRecords && this.subRecords.length > 0) {
          this.selectedSubRecord = this.subRecords[0];
          this.showFlyout = true;
        }
      },
      error: (error) => {
        this.errorMessage = `Error loading sub records: ${error}`;
        this.subRecords = [];
      }
    });
  }

  onSubRecordSelected(subRecord: FlaggedContactSubContactModel): void {
    this.selectedSubRecord = subRecord;
    this.showFlyout = true;
  }

  closeFlyout(): void {
    this.showFlyout = false;
    this.selectedSubRecord = null;
  }

  onExcludeClicked(event: Event, subRecord: FlaggedContactSubContactModel | null): void {
    event.preventDefault();
    event.stopPropagation();

    if (!subRecord) {
      this.errorMessage = 'No contact selected';
      return;
    }

    if (!this.sourceId) {
      this.errorMessage = 'Source ID is missing. Please refresh the page.';
      return;
    }

    if (!subRecord.contactId) {
      this.errorMessage = 'Contact ID is missing. Cannot exclude contact.';
      return;
    }

    if (!subRecord.ofacContactId) {
      this.errorMessage = 'OFAC Contact ID is missing. Cannot exclude contact.';
      return;
    }

    this.isExcluding = true;
    this.errorMessage = null;

    let applicationId: string | null = subRecord.applicationId || null;
    if (!applicationId && this.selectedHeaderRecord?.applicationId) {
      applicationId = this.selectedHeaderRecord.applicationId;
    }

    this.ofacRecordsService.addExclusion(
      this.sourceId,
      subRecord.contactId,
      subRecord.ofacContactId,
      applicationId
    ).subscribe({
      next: () => {
        this.isExcluding = false;
        this.loadData();
      },
      error: (error) => {
        this.isExcluding = false;
        const errorMsg = error?.message || error?.toString() || 'Unknown error';
        this.errorMessage = `Error excluding contact: ${errorMsg}`;
      }
    });
  }

  onSyncStatusClicked(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const record = this.contextMenuRecord;
    this.closeContextMenu();

    if (!record) {
      this.errorMessage = 'No contact selected';
      return;
    }

    if (!this.sourceId) {
      this.errorMessage = 'Source ID is missing. Please refresh the page.';
      return;
    }

    if (!record.contactId) {
      this.errorMessage = 'Contact ID is missing. Cannot sync status.';
      return;
    }

    this.isSyncingStatus = true;
    this.errorMessage = null;
    this.successMessage = null;

    this.ofacRecordsService.sendOfacStatusChangeMessage(
      this.sourceId,
      record.contactId,
      true
    ).subscribe({
      next: () => {
        this.isSyncingStatus = false;
        this.successMessage = `OFAC match status synced for contact ${record.contactId}.`;
      },
      error: (error) => {
        this.isSyncingStatus = false;
        const errorMsg = error?.message || error?.toString() || 'Unknown error';
        this.errorMessage = `Error syncing status: ${errorMsg}`;
      }
    });
  }

  isSubRecordSelected(subRecord: FlaggedContactSubContactModel): boolean {
    return this.selectedSubRecord !== null &&
           this.selectedSubRecord.contactId === subRecord.contactId &&
           this.selectedSubRecord.firstName === subRecord.firstName &&
           this.selectedSubRecord.lastName === subRecord.lastName &&
           this.selectedSubRecord.middleNames === subRecord.middleNames;
  }

  onHeaderSort(event: Event, column: string): void {
    event.stopPropagation();
    if (this.headerSortColumn === column) {
      // Toggle direction if clicking the same column
      this.headerSortDirection = this.headerSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, start with ascending
      this.headerSortColumn = column;
      this.headerSortDirection = 'asc';
    }
    this.applyHeaderSorting();
  }

  applyHeaderSorting(): void {
    if (!this.headerRecords || !this.headerSortColumn) {
      return;
    }

    this.headerRecords.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (this.headerSortColumn) {
        case 'contactId':
          aValue = a.contactId || '';
          bValue = b.contactId || '';
          break;
        case 'lastName':
          aValue = a.lastName || '';
          bValue = b.lastName || '';
          break;
        case 'firstName':
          aValue = a.firstName || '';
          bValue = b.firstName || '';
          break;
        case 'middleNames':
          aValue = a.middleNames || '';
          bValue = b.middleNames || '';
          break;
        case 'address':
          aValue = a.address || '';
          bValue = b.address || '';
          break;
        case 'city':
          aValue = a.city || '';
          bValue = b.city || '';
          break;
        case 'state':
          aValue = a.state || '';
          bValue = b.state || '';
          break;
        default:
          return 0;
      }

      const comparison = typeof aValue === 'string' 
        ? aValue.localeCompare(bValue)
        : (aValue < bValue ? -1 : aValue > bValue ? 1 : 0);

      return this.headerSortDirection === 'asc' ? comparison : -comparison;
    });
  }

  getHeaderSortIcon(column: string): string {
    if (this.headerSortColumn !== column) {
      return '↕';
    }
    return this.headerSortDirection === 'asc' ? '↑' : '↓';
  }
}
